import { inLifePaths } from '@in-life/paths';
import { BodyText, Box, Flex, Link } from '@utilitywarehouse/hearth-react';
import dayjs from 'dayjs';
import { MouseEvent, MouseEventHandler, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { DialogID, useDialog } from '@components/Dialog';
import { EXTERNAL_LINKS } from '@constants/externalLinks';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useMode } from '@store/journey/selectors';
import { JOURNEY_MODE } from '@store/journey/slice';
import { stripTrailingSlash } from '@utils/routing/stripTrailingSlash';

import './styles.scss';

interface LinkProps {
  href?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement> | undefined;
  children: ReactNode;
  testId?: string;
}

const Cookies = () => {
  const handleOnClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!window.OneTrust?.ToggleInfoDisplay) {
      return;
    }

    e.preventDefault();
    window.OneTrust.ToggleInfoDisplay();
  };

  return (
    <ItemLink
      href={EXTERNAL_LINKS.cookiesPolicy}
      onClick={handleOnClick}
      testId="footer-cookies-link"
    >
      Cookies
    </ItemLink>
  );
};

const ItemLink = ({ href, onClick, children, testId }: LinkProps) => (
  <li>
    <Link
      href={href}
      rel="noreferrer noopener"
      target="_blank"
      hideOpenIcon
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </Link>
  </li>
);

const Footer = () => {
  const mode = useMode();
  const { appointment } = useAppointmentContext();
  const location = useLocation();
  const { basketId } = useBasketContext();
  const startAgainDialog = useDialog(DialogID.START_AGAIN);
  const inLifeStartAgainDialog = useDialog(DialogID.IN_LIFE_START_AGAIN);

  const isAppointment = appointment?.id;
  const isInLifeMode = mode === JOURNEY_MODE.INLIFE;
  const strippedPathname = stripTrailingSlash(location.pathname);
  const hideInLifeStartAgain =
    isInLifeMode &&
    (strippedPathname === inLifePaths.energyTariff ||
      strippedPathname === inLifePaths.thankYou);

  const handleStartAgain = () => {
    if (isInLifeMode) {
      return inLifeStartAgainDialog.setOpen(true);
    }

    startAgainDialog.setOpen(true);
  };

  return (
    <Box className="layout-footer__container">
      <Flex
        justifyContent="between"
        className="wrapper"
        direction={{ mobile: 'column', desktop: 'row' }}
        spacing="lg"
      >
        <BodyText as="div" size="sm">
          <p>&copy; Utility Warehouse Limited {dayjs().year()} (04594421).</p>
          <p>Authorised by the Financial Conduct Authority.</p>
          {Boolean(basketId) && (
            <p data-testid="footer-basket-id">
              Basket ID: <span className="layout-footer__id">{basketId}</span>
            </p>
          )}
        </BodyText>

        <nav>
          <ul className="layout-footer__links-list">
            <Cookies />
            <ItemLink href={EXTERNAL_LINKS.privacyPolicy}>Privacy</ItemLink>
            <ItemLink href={EXTERNAL_LINKS.termsAndConditions}>
              Terms & Conditions
            </ItemLink>

            {(basketId || isAppointment) && !hideInLifeStartAgain && (
              <ItemLink
                onClick={handleStartAgain}
                testId="footer-start-again-link"
              >
                Start Again
              </ItemLink>
            )}
          </ul>
        </nav>
      </Flex>
    </Box>
  );
};

export { Footer };
